import { AlertCircle, Check, Info } from "lucide-react";
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ScaledThumbnail } from "./ScaledThumbnail";

interface ExecutiveCashPositionCardThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

const FORECASTS = [
  { value: "£198,400", label: "30-Day Forecast" },
  { value: "£60,600", label: "90-Day Forecast" },
];

const OBLIGATIONS = [
  { value: "3", label: "Overdue Invoices", detail: "£24,500.00" },
  { value: "5", label: "Upcoming Payments", detail: "£38,200.00" },
];

export function ExecutiveCashPositionCardThumbnail({
  isHovered = false,
  alwaysVisible = false,
}: ExecutiveCashPositionCardThumbnailProps) {
  return (
    <ScaledThumbnail
      isHovered={isHovered}
      alwaysVisible={alwaysVisible}
      contentWidth={320}
    >
      <Card
        className="w-[320px]"
        style={{ borderLeft: "3px solid var(--mc-success-border)" }}
      >
        <CardContent className="p-4 flex flex-col gap-4">
          <div className="flex items-center gap-1">
            <span className="text-base font-semibold text-neutral-900">
              Cash Position
            </span>
            <Info className="h-3 w-3 text-muted-foreground" />
          </div>

          <div className="flex flex-col items-center gap-1 py-3">
            <span className="text-2xl font-bold text-neutral-900 tracking-tight">
              £247,000
            </span>
            <span className="text-xs text-neutral-900">
              Current Cash Balance
            </span>
          </div>

          <div
            className="flex items-center gap-2 rounded-lg px-2 py-1.5"
            style={{
              backgroundColor: "var(--mc-success-bg)",
              border: "1px solid var(--mc-success-border)",
            }}
          >
            <div
              className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "var(--mc-success-icon)" }}
            >
              <Check className="w-2.5 h-2.5 text-white" />
            </div>
            <div>
              <span
                className="text-xs font-semibold"
                style={{ color: "var(--mc-success-icon)" }}
              >
                Healthy
              </span>
              <span
                className="text-xs ml-1"
                style={{ color: "var(--mc-success-icon)" }}
              >
                4.2 months coverage
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {FORECASTS.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-neutral-200 px-2 py-1.5 text-center"
              >
                <p className="text-sm font-semibold text-neutral-900">
                  {item.value}
                </p>
                <p className="text-[10px] text-neutral-900 mt-0.5">
                  {item.label}
                </p>
              </div>
            ))}
          </div>

          <hr className="border-neutral-200" />

          <div
            className="flex items-center gap-1.5 rounded-lg px-2 py-1.5"
            style={{
              backgroundColor: "var(--mc-warning-bg)",
              border: "1px solid var(--mc-warning-border)",
            }}
          >
            <div
              className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "var(--mc-warning-icon)" }}
            >
              <AlertCircle className="w-2.5 h-2.5 text-white" />
            </div>
            <span
              className="text-[11px] font-medium"
              style={{ color: "var(--mc-warning-icon)" }}
            >
              3 Overdue Invoices
            </span>
            <span
              className="text-[11px]"
              style={{ color: "var(--mc-warning-icon)" }}
            >
              · £24,500.00
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {OBLIGATIONS.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-neutral-200 px-2 py-1.5 text-center"
              >
                <p className="text-sm font-semibold text-neutral-900">
                  {item.value}
                </p>
                <p className="text-[10px] text-neutral-900 mt-0.5">
                  {item.label}
                </p>
                <p className="text-[10px] text-neutral-500 mt-0.5">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </ScaledThumbnail>
  );
}

export default ExecutiveCashPositionCardThumbnail;
